import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EMPTY = {
  name: "", location: "", city: "", description: "", description_en: "",
  max_guests: 2, bedrooms: 1, bathrooms: 1, price_per_night: 0, pricing_rules: [],
  amenities: [], photos: [], house_rules: [], wifi_name: "", wifi_password: "",
  checkin_instructions: "", airbnb_url: "", booking_url: "", airbnb_ical: "", booking_ical: "",
  map_address: "", guest_guide: "", active: true,
};

const newRule = () => ({
  id: (window.crypto?.randomUUID?.() || String(Date.now() + Math.random())),
  label: "", date_from: "", date_to: "", weekday_price: "", weekend_price: "",
});

const listToText = (arr) => (arr || []).join("\n");
const textToList = (txt) => txt.split("\n").map((x) => x.trim()).filter(Boolean);

const Field = ({ label, value, onChange, type = "text", area, full, testid }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="overline text-[10px] text-muted-foreground">{label}</span>
    {area ? (
      <textarea rows={area} data-testid={testid} className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none"
        value={value} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input type={type} data-testid={testid} className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none"
        value={value} onChange={(e) => onChange(e.target.value)} />
    )}
  </label>
);

export default function AdminApartments() {
  const [apartments, setApartments] = useState([]);
  const [editing, setEditing] = useState(null); // apt object or EMPTY for new
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/apartments").then((r) => setApartments(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (apt) => setEditing({ ...EMPTY, ...apt });

  const save = async () => {
    setSaving(true);
    const payload = {
      ...editing,
      max_guests: Number(editing.max_guests),
      bedrooms: Number(editing.bedrooms),
      bathrooms: Number(editing.bathrooms),
      price_per_night: Number(editing.price_per_night) || 0,
      pricing_rules: (Array.isArray(editing.pricing_rules) ? editing.pricing_rules : []).map((r) => ({
        ...r,
        weekday_price: Number(r.weekday_price) || 0,
        weekend_price: Number(r.weekend_price) || 0,
      })),
      amenities: Array.isArray(editing.amenities) ? editing.amenities : textToList(editing.amenities),
      photos: Array.isArray(editing.photos) ? editing.photos : textToList(editing.photos),
      house_rules: Array.isArray(editing.house_rules) ? editing.house_rules : textToList(editing.house_rules),
    };
    try {
      if (editing.id) await api.put(`/admin/apartments/${editing.id}`, payload);
      else await api.post("/admin/apartments", payload);
      toast.success("Appartamento salvato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo appartamento?")) return;
    await api.delete(`/admin/apartments/${id}`);
    toast.success("Eliminato");
    load();
  };

  const set = (k, v) => setEditing({ ...editing, [k]: v });

  const rules = (editing?.pricing_rules) || [];
  const setRules = (r) => set("pricing_rules", r);
  const addRule = () => setRules([...rules, newRule()]);
  const updRule = (i, k, v) => setRules(rules.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const delRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

  return (
    <div data-testid="admin-apartments">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif font-light text-4xl text-petrol">Appartamenti</h1>
          <p className="text-muted-foreground">Gestisci strutture, foto, servizi e link OTA</p>
        </div>
        <button onClick={openNew} data-testid="add-apartment" className="flex items-center gap-2 bg-petrol text-white px-5 py-3 rounded-sm hover:bg-[#1A3A41] transition-colors">
          <Plus size={18} strokeWidth={1.5} /> Nuovo
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {apartments.map((a) => (
          <div key={a.id} className="bg-card border border-line rounded-sm overflow-hidden" data-testid={`admin-apt-${a.id}`}>
            <div className="aspect-[16/10] bg-beige overflow-hidden">
              {a.photos?.[0] && <img src={a.photos[0]} alt={a.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-5">
              <h3 className="font-serif text-xl text-petrol">{a.name}</h3>
              <p className="text-sm text-muted-foreground">{a.location}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openEdit(a)} data-testid={`edit-apt-${a.id}`} className="flex-1 flex items-center justify-center gap-2 border border-petrol text-petrol py-2 rounded-sm hover:bg-beige text-sm transition-colors">
                  <Pencil size={14} /> Modifica
                </button>
                <button onClick={() => remove(a.id)} data-testid={`delete-apt-${a.id}`} className="px-3 border border-line text-destructive rounded-sm hover:bg-destructive/5 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setEditing(null)}>
          <div className="bg-cream rounded-sm w-full max-w-3xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="apartment-editor">
            <div className="flex items-center justify-between p-6 border-b border-line sticky top-0 bg-cream">
              <h2 className="font-serif text-2xl text-petrol">{editing.id ? "Modifica appartamento" : "Nuovo appartamento"}</h2>
              <button onClick={() => setEditing(null)}><X size={22} className="text-muted-foreground" /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-5">
              <Field label="Nome struttura" value={editing.name} onChange={(v) => set("name", v)} testid="editor-name" />
              <Field label="Posizione (es. Centro, Roma)" value={editing.location} onChange={(v) => set("location", v)} />
              <Field label="Città" value={editing.city} onChange={(v) => set("city", v)} />
              <Field label="Ospiti max" value={editing.max_guests} onChange={(v) => set("max_guests", v)} type="number" />
              <Field label="Camere" value={editing.bedrooms} onChange={(v) => set("bedrooms", v)} type="number" />
              <Field label="Bagni" value={editing.bathrooms} onChange={(v) => set("bathrooms", v)} type="number" />
              <Field label="Indirizzo mappa" value={editing.map_address} onChange={(v) => set("map_address", v)} />

              {/* Prezzi dinamici */}
              <div className="sm:col-span-2 border border-line rounded-sm p-4 bg-card" data-testid="pricing-rules">
                <div className="flex items-center justify-between mb-1">
                  <p className="overline text-gold">Prezzi a notte (dinamici)</p>
                  <button type="button" onClick={addRule} data-testid="add-price-rule" className="flex items-center gap-1.5 text-xs border border-petrol text-petrol rounded-sm px-3 py-1.5 hover:bg-beige transition-colors">
                    <Plus size={13} /> Aggiungi regola
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mb-4">Imposta un prezzo per periodo e distingui weekend (ven-sab) da infrasettimanale. Lascia le date vuote per una <strong>tariffa standard</strong> valida tutto l'anno. Senza date specifiche, le notti non coperte da regole non avranno prezzo.</p>
                {rules.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna regola. Aggiungi almeno una tariffa standard (senza date) o per periodo.</p>}
                <div className="space-y-3">
                  {rules.map((r, i) => (
                    <div key={r.id || i} className="grid grid-cols-12 gap-2 items-end border-b border-line/60 pb-3" data-testid={`price-rule-${i}`}>
                      <label className="col-span-12 sm:col-span-3 block">
                        <span className="text-[10px] text-muted-foreground">Etichetta</span>
                        <input value={r.label} onChange={(e) => updRule(i, "label", e.target.value)} placeholder="es. Alta stagione" data-testid={`rule-label-${i}`}
                          className="w-full border border-line rounded-sm px-2 py-1.5 mt-0.5 text-sm text-petrol bg-cream outline-none" />
                      </label>
                      <label className="col-span-6 sm:col-span-2 block">
                        <span className="text-[10px] text-muted-foreground">Dal</span>
                        <input type="date" value={r.date_from} onChange={(e) => updRule(i, "date_from", e.target.value)} data-testid={`rule-from-${i}`}
                          className="w-full border border-line rounded-sm px-2 py-1.5 mt-0.5 text-sm text-petrol bg-cream outline-none" />
                      </label>
                      <label className="col-span-6 sm:col-span-2 block">
                        <span className="text-[10px] text-muted-foreground">Al</span>
                        <input type="date" value={r.date_to} onChange={(e) => updRule(i, "date_to", e.target.value)} data-testid={`rule-to-${i}`}
                          className="w-full border border-line rounded-sm px-2 py-1.5 mt-0.5 text-sm text-petrol bg-cream outline-none" />
                      </label>
                      <label className="col-span-5 sm:col-span-2 block">
                        <span className="text-[10px] text-muted-foreground">Infrasett. €</span>
                        <input type="number" value={r.weekday_price} onChange={(e) => updRule(i, "weekday_price", e.target.value)} data-testid={`rule-weekday-${i}`}
                          className="w-full border border-line rounded-sm px-2 py-1.5 mt-0.5 text-sm text-petrol bg-cream outline-none" />
                      </label>
                      <label className="col-span-5 sm:col-span-2 block">
                        <span className="text-[10px] text-muted-foreground">Weekend €</span>
                        <input type="number" value={r.weekend_price} onChange={(e) => updRule(i, "weekend_price", e.target.value)} data-testid={`rule-weekend-${i}`}
                          className="w-full border border-line rounded-sm px-2 py-1.5 mt-0.5 text-sm text-petrol bg-cream outline-none" />
                      </label>
                      <div className="col-span-2 sm:col-span-1 flex justify-end">
                        <button type="button" onClick={() => delRule(i)} data-testid={`del-rule-${i}`} className="text-destructive hover:opacity-70 p-1.5"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Field label="Descrizione (IT)" value={editing.description} onChange={(v) => set("description", v)} area={3} full />
              <Field label="Descrizione (EN)" value={editing.description_en} onChange={(v) => set("description_en", v)} area={3} full />
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Foto (un URL per riga)</span>
                <textarea rows={3} className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none"
                  value={Array.isArray(editing.photos) ? listToText(editing.photos) : editing.photos}
                  onChange={(e) => set("photos", e.target.value)} data-testid="editor-photos" />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Servizi (uno per riga)</span>
                <textarea rows={3} className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none"
                  value={Array.isArray(editing.amenities) ? listToText(editing.amenities) : editing.amenities}
                  onChange={(e) => set("amenities", e.target.value)} data-testid="editor-amenities" />
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Regole della casa (una per riga)</span>
                <textarea rows={2} className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none"
                  value={Array.isArray(editing.house_rules) ? listToText(editing.house_rules) : editing.house_rules}
                  onChange={(e) => set("house_rules", e.target.value)} />
              </label>
              <Field label="Nome rete Wi-Fi" value={editing.wifi_name} onChange={(v) => set("wifi_name", v)} />
              <Field label="Password Wi-Fi" value={editing.wifi_password} onChange={(v) => set("wifi_password", v)} />
              <Field label="Istruzioni self check-in" value={editing.checkin_instructions} onChange={(v) => set("checkin_instructions", v)} area={2} full />
              <Field label="Guest Guide (consigli locali)" value={editing.guest_guide} onChange={(v) => set("guest_guide", v)} area={2} full />
              <div className="sm:col-span-2 border-t border-line pt-4 mt-2">
                <p className="overline text-gold mb-3">Channel Manager / OTA — Sincronizzazione iCal</p>
              </div>
              <Field label="iCal Airbnb (URL .ics da importare)" value={editing.airbnb_ical} onChange={(v) => set("airbnb_ical", v)} />
              <Field label="iCal Booking (URL .ics da importare)" value={editing.booking_ical} onChange={(v) => set("booking_ical", v)} />
            </div>
            <div className="p-6 border-t border-line flex justify-end gap-3 sticky bottom-0 bg-cream">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-line text-petrol rounded-sm hover:bg-beige transition-colors">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="save-apartment" className="px-6 py-2.5 bg-petrol text-white rounded-sm hover:bg-[#1A3A41] transition-colors disabled:opacity-60">
                {saving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
